import { getTrackerDisplayLabel } from "@/features/diaries/utils/displayLabels";
import { components as c } from "@/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";
import { Button } from "@patient-watch/ui/button";
import { Checkbox } from "@patient-watch/ui/checkbox";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import { scheduleStepDisplayLabel } from "@/features/entries/services/timingLabelDerivation";
import { useStopSingleTracker } from "@/features/trackers/api/stopSingleTracker";
import type { StatefulTrackerViewT } from "@patient-watch/types";
import dayjs from "dayjs";
import { anyTrackerHasLockedUpcoming } from "@/features/patients/update/anyTrackerHasLockedUpcoming";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { ClipboardList, Clock } from "lucide-react";

function ExistingTrackerUpcomingEntries({ tracker }: { tracker: StatefulTrackerViewT }) {
    const copy = usePatientsCopy();
    const upcomingList =
        tracker.upcoming_entries?.filter(
            (entry): entry is NonNullable<typeof entry> => entry != null && !!entry.is_locked
        ) ?? [];

    const sortByDueAt = (a: (typeof upcomingList)[number], b: (typeof upcomingList)[number]) =>
        dayjs(a.due_at).valueOf() - dayjs(b.due_at).valueOf();

    const upcomingSorted = [...upcomingList].sort(sortByDueAt);
    const visibleEntries =
        upcomingSorted.length > 6
            ? [
                  ...upcomingSorted.slice(0, 3),
                  { kind: "summary" as const, hiddenCount: upcomingSorted.length - 5 },
                  ...upcomingSorted.slice(-2),
              ]
            : upcomingSorted;

    return (
        <div className="space-y-2 mt-3 pt-3 border-slate-200/80 dark:border-slate-600/60 border-t w-full text-left">
            {upcomingSorted.length > 0 ? (
                <>
                    <p className="flex items-center gap-1.5 font-medium text-slate-700 dark:text-slate-200 text-xs">
                        <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        {copy.upcomingEntries}
                    </p>
                    <ul className="space-y-1 pl-5 text-slate-600 dark:text-slate-300 text-xs list-disc">
                        {visibleEntries.map((entry, index) => {
                            if ("kind" in entry) {
                                return (
                                    <li key="upcoming-summary" className="text-slate-500 dark:text-slate-400">
                                        {copy.moreEntriesSummary(entry.hiddenCount)}
                                    </li>
                                );
                            }
                            return (
                                <li key={`up-${entry.timing_id}-${entry.schedule_index}-${index}`}>
                                    <span className="font-medium text-slate-700 dark:text-slate-200">
                                        {scheduleStepDisplayLabel(entry)}
                                    </span>
                                    {" · "}
                                    {copy.unlocks(dayjs(entry.due_at).format("DD MMM YYYY"))}
                                </li>
                            );
                        })}
                    </ul>
                </>
            ) : (
                <p className="text-slate-500 dark:text-slate-400 text-xs">{copy.noUpcomingEntries}</p>
            )}
        </div>
    );
}

const TemplateExistingTrackersPage = () => {
    const copy = usePatientsCopy();
    const { diaryId } = Route.useParams();
    const search = Route.useSearch();
    const navigate = useNavigate();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const stopTracker = useStopSingleTracker();

    const activeTrackers = diary?.trackers_view?.filter((t) => !t.end_date) ?? [];

    const [pausedIds, setPausedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!diary) return;
        if (activeTrackers.length === 0 || !anyTrackerHasLockedUpcoming(activeTrackers)) {
            void navigate({
                to: "/patients/$diaryId/update/from-template",
                params: { diaryId },
                search,
            });
        }
        // Intentionally only when diary data is available; matches manual `existing-trackers` skip behaviour.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-running on every search identity
    }, [diary]);

    const toggle = (trackerId: string) => {
        setPausedIds((prev) => {
            const next = new Set(prev);
            if (next.has(trackerId)) {
                next.delete(trackerId);
            } else {
                next.add(trackerId);
            }
            return next;
        });
    };

    const pausedCount = pausedIds.size;
    const totalCount = activeTrackers.length;

    const getButtonLabel = () => {
        if (stopTracker.isPending) return copy.stopping;
        if (totalCount === 0 || pausedCount === 0) return copy.continueWithAllQuestionnaires;
        if (pausedCount === totalCount) return copy.stopAllCurrentQuestionnaires;
        const names = activeTrackers.filter((t) => pausedIds.has(t.tracker_id)).map((t) => getTrackerDisplayLabel(t));
        return names.length === 1
            ? copy.stopOnly(names[0]!)
            : copy.stopJoined(`${names.slice(0, -1).join(", ")} & ${names.at(-1)}`);
    };

    const handleContinue = async () => {
        for (const trackerId of pausedIds) {
            const tracker = activeTrackers.find((t) => t.tracker_id === trackerId);
            if (tracker?.shared_attributes?.shared_attribute_id) {
                await stopTracker.mutateAsync({
                    tracker_id: trackerId,
                    diary_id: diaryId,
                    shared_attribute_id: tracker.shared_attributes.shared_attribute_id,
                });
            }
        }

        void navigate({
            to: "/patients/$diaryId/update/from-template",
            params: { diaryId },
            search,
        });
    };

    if (!diary || activeTrackers.length === 0 || !anyTrackerHasLockedUpcoming(activeTrackers)) {
        return (
            <div className="space-y-8 max-w-3xl">
                <c.cc.Title words={copy.existingQuestionnaires} description={copy.checkingActiveBeforeTemplate} />
            </div>
        );
    }

    return (
        <div className="space-y-8 max-w-3xl">
            <c.cc.Title words={copy.existingQuestionnaires} description={copy.templateExistingTrackersDescription} />

            <div className="space-y-2">
                {activeTrackers.map((tracker) => {
                    const name = getTrackerDisplayLabel(tracker);
                    const isStopped = pausedIds.has(tracker.tracker_id);
                    return (
                        <div
                            key={tracker.tracker_id}
                            role="checkbox"
                            aria-checked={isStopped}
                            tabIndex={0}
                            onClick={() => toggle(tracker.tracker_id)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    toggle(tracker.tracker_id);
                                }
                            }}
                            className={`flex flex-col gap-0 p-3 border-2 rounded-lg transition-colors cursor-pointer hover:bg-white/40 dark:hover:bg-white/5 ${
                                isStopped
                                    ? "border-indigo-600 bg-white/30 dark:bg-white/10"
                                    : "border-transparent bg-white/30 dark:bg-white/5"
                            }`}
                        >
                            <div className="flex items-center gap-3 -m-1 p-1 rounded-md w-full">
                                <ClipboardList
                                    size={18}
                                    className={`shrink-0 ${isStopped ? "text-indigo-600" : "text-slate-400"}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm">{name}</p>
                                </div>
                                <Checkbox
                                    id={`tt-${tracker.tracker_id}`}
                                    checked={isStopped}
                                    onCheckedChange={() => toggle(tracker.tracker_id)}
                                    onClick={(event) => event.stopPropagation()}
                                    aria-label={copy.stopTrackerAria(name)}
                                />
                            </div>
                            <ExistingTrackerUpcomingEntries tracker={tracker} />
                        </div>
                    );
                })}
            </div>

            <Button size="lg" className="w-full" onClick={handleContinue} disabled={stopTracker.isPending}>
                {getButtonLabel()}
            </Button>
        </div>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/template-existing-trackers")({
    component: TemplateExistingTrackersPage,
    validateSearch: updateDiaryFlowSearchSchema,
});
